import { useParams } from "react-router-dom";
import { FoundationProgramContent } from "./foundationProgramContent";
import { FoundationProgramConstants } from "../../common/data/foundationPrograms";
import { IFoundationProgram, IFoundationProgramFile, IProgramClass } from "../../common/models/foundationProgram";
import { Box, Card, CardContent, Chip, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import { ProgramDetailsProvider, ProgramFilesProvider } from "./foundationProgramContext";
import FoundationProgramService from "../../schoolPrograms/foundationProgram/foundationProgramService";
import { IAPIResponse, IHTTPResponse } from "../../common/models/httpModels";
import { IFoundationProgramDetailsContextModel, IFoundationProgramFeatureProps, IFoundationProgramFilesContextModel } from "../../schoolPrograms/foundationProgram/foundationProgramModels";
import { toast } from "react-toastify";
import React, { useEffect, useState, useMemo } from "react";
import { FoundationProgramUIProvider, useFoundationProgramUIContext } from "./foundationProgramUIContext";


export const FoundationPrograms = (props: IFoundationProgramFeatureProps) => {
    const { programName, className } = useParams();

    const [programDetailsContext, setProgramDetailsContext] = useState<IFoundationProgramDetailsContextModel>({
        programId: 0,
        classId: 0,
        isDeleteFilesVisible: props.isDeleteFilesVisible,
        isAddFilesVisible: props.isAddFilesVisible,
        programDetails: undefined,
        classDetails: undefined
    });

    const [programFiles, setProgramFiles] = useState<IFoundationProgramFile[]>([]);

    const addProgramFiles = (programFiles: IFoundationProgramFile[]) => {
        setProgramFiles((prevFiles) => [...prevFiles, ...programFiles]);
    }

    const deleteProgramFile = (programFile: IFoundationProgramFile) => {
        setProgramFiles((prevFiles) => prevFiles.filter(i => i.fileId !== programFile.fileId));
    }

    const updateFiles = (programFiles: IFoundationProgramFile[]) => {
        setProgramFiles(programFiles);
    }

    const reloadProgramFilesContext = () => {
        if (!programDetailsContext.programDetails?.programId || !programDetailsContext.classDetails?.classId) {
            return;
        }
        loadProgramFilesContext(programDetailsContext.programDetails, programDetailsContext.classDetails);
    }

    const loadProgramFilesContext = (foundationProgram: IFoundationProgram, programClassDetails: IProgramClass) => {
        setFilesLoading(true);
        FoundationProgramService.getFiles(foundationProgram.programId, programClassDetails.classId)
            .then((result: IHTTPResponse<IAPIResponse<IFoundationProgramFile>>) => {
                if (result.data.errors?.length > 0) {
                    toast.error(result.data.errors[0].message);
                    return;
                }

                setProgramFiles(result.data.list);
            })
            .catch((error) => {
                toast.error('Failed to load program files');
                console.error('Error loading program files:', error);
            })
            .finally(() => setFilesLoading(false));
    }

    const programFilesContext = useMemo(() => ({
        programFiles,
        addFiles: addProgramFiles,
        deleteFile: deleteProgramFile,
        reloadFiles: reloadProgramFilesContext,
        updateFiles: updateFiles
    }), [programFiles]);

    // Track worksheet/subject for UI context (lifted state)
    const [activeWorkSheetId, setActiveWorkSheetId] = useState<number | null>(null);
    const [activeSubjectId, setActiveSubjectId] = useState<number | null>(null);
    const [filesLoading, setFilesLoading] = useState<boolean>(false);

    const updateActiveWorkSheetId = (id: number | null) => {
        setActiveWorkSheetId(id);
        if (id === null) {
            setActiveSubjectId(null); // Reset subject when worksheet is cleared    
        } else {
            const workSheet = programDetailsContext.classDetails?.workSheets.find(ws => ws.workSheetId === id);
            if (workSheet && Array.isArray(workSheet.subjects) && workSheet.subjects.length > 0) {
                setActiveSubjectId(workSheet.subjects[0].subjectId); // Set first subject of the worksheet
            } else {
                setActiveSubjectId(null); // No subjects available, reset subject
            }
        }
    }
    useEffect(() => {
        if (programName && className) {
            const program = FoundationProgramConstants.find(i => i.programName === programName);

            if (program) {
                const classInfo = program.classList.find(i => i.className === className);

                if (classInfo) {
                    setProgramDetailsContext(prev => ({
                        ...prev,
                        programId: program.programId,
                        classId: classInfo.classId,
                        programDetails: program,
                        classDetails: classInfo
                    }));

                    // Safely set initial worksheet and subject for UI context
                    const firstWorkSheet = Array.isArray(classInfo.workSheets) && classInfo.workSheets.length > 0 ? classInfo.workSheets[0] : null;
                    setActiveWorkSheetId(firstWorkSheet?.workSheetId ?? null);

                    const firstSubject = firstWorkSheet && Array.isArray(firstWorkSheet.subjects) && firstWorkSheet.subjects.length > 0
                        ? firstWorkSheet.subjects[0]
                        : null;
                    setActiveSubjectId(firstSubject?.subjectId ?? null);

                    loadProgramFilesContext(program, classInfo);
                } else {
                    setProgramDetailsContext(prev => ({
                        ...prev,
                        programId: 0,
                        classId: 0,
                        programDetails: undefined,
                        classDetails: undefined
                    }));
                    setActiveWorkSheetId(null);
                    setActiveSubjectId(null);
                    toast.error("Class not found");
                }
            } else {
                setProgramDetailsContext(prev => ({
                    ...prev,
                    programId: 0,
                    classId: 0,
                    programDetails: undefined,
                    classDetails: undefined
                }));
                setActiveWorkSheetId(null);
                setActiveSubjectId(null);
                toast.error("Program not found");
            }
        }
    }, [programName, className]);

    if (!programDetailsContext.programDetails || !programDetailsContext.classDetails) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <Typography color="text.secondary">Select a program and class from the menu.</Typography>
            </Box>
        );
    }

    return (
        <Grid container sx={{ height: "100%" }}>
            <Grid item xs={12}>
                <Card sx={{ height: "100%" }}>
                    {/* Page header */}
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {programDetailsContext.programDetails?.programName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Files &amp; Resources
                            </Typography>
                        </Box>
                        <Chip
                            label={programDetailsContext.classDetails?.className}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                        <Chip
                            label={`${programFiles.length} file${programFiles.length !== 1 ? 's' : ''}`}
                            variant="outlined"
                            size="small"
                            color="default"
                        />
                    </Box>
                    <Divider />
                    {filesLoading && <LinearProgress />}
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <ProgramDetailsProvider value={programDetailsContext}>
                            <ProgramFilesProvider value={programFilesContext}>
                                <FoundationProgramUIProvider
                                    activeWorkSheetId={activeWorkSheetId}
                                    setActiveWorkSheetId={updateActiveWorkSheetId}
                                    activeSubjectId={activeSubjectId}
                                    setActiveSubjectId={setActiveSubjectId}
                                >
                                    <FoundationProgramContent />
                                </FoundationProgramUIProvider>
                            </ProgramFilesProvider>
                        </ProgramDetailsProvider>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}